<template>
    <div class="flex items-center gap-3 sm:gap-4">
        <router-link
            v-if="isAuthenticated"
            to="/my-account"
            class="hidden lg:flex items-center gap-2 rounded-full border border-[#E3E5E6] dark:border-white/15 px-3 py-2 text-sm leading-none text-title dark:text-white transition-all duration-300 hover:border-primary hover:text-primary"
        >
            <span class="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold uppercase flex-none">{{ userInitial }}</span>
            <span class="max-w-[84px] truncate font-medium">{{ displayName }}</span>
        </router-link>
        <router-link v-else to="/login" class="text-lg leading-none text-title dark:text-white transition-all duration-300 hover:text-primary hidden lg:block">Login</router-link>

        <button class="hdr_search_btn" aria-label="search" @click="toggleSearch">
            <i class="mdi mdi-magnify text-title dark:text-white text-[24px] sm:text-[28px]"></i>
        </button>
        <div
            v-if="searchOpen"
            ref="searchPopupRef"
            class="search_popup w-80 sm:w-96 md:w-[420px] absolute z-50 top-full right-0 sm:right-10 xl:right-0 bg-white dark:bg-title p-5 md:p-[24px] border border-primary"
        >
            <div class="flex items-center gap-3 border border-bdr-clr dark:border-bdr-clr-drk px-3 py-2">
                <i class="mdi mdi-magnify text-title dark:text-white text-[22px]"></i>
                <input
                    ref="searchInputRef"
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search products..."
                    class="flex-1 bg-transparent outline-none text-base text-title dark:text-white placeholder-paragraph dark:placeholder-white-light"
                    @keydown.escape="closeSearch"
                />
                <button v-if="searchQuery" type="button" class="text-paragraph dark:text-white-light text-sm" aria-label="clear" @click="searchQuery = ''">
                    Clear
                </button>
            </div>

            <div class="mt-4 max-h-[60vh] overflow-y-auto pr-1">
                <p v-if="searchLoading" class="text-sm text-paragraph dark:text-white-light">Searching...</p>
                <p v-else-if="searchError" class="text-sm text-red-500">{{ searchError }}</p>
                <p v-else-if="!searchQuery.trim()" class="text-sm text-paragraph dark:text-white-light">Type to search products by name, SKU, or description.</p>
                <p v-else-if="!searchResults.length" class="text-sm text-paragraph dark:text-white-light">No products match "{{ searchQuery }}".</p>
                <ul v-else class="space-y-[15px]">
                    <li v-for="item in searchResults" :key="item.id" class="flex items-center gap-[15px] pb-[15px] border-b border-bdr-clr dark:border-bdr-clr-drk last:border-b-0 last:pb-0">
                        <router-link :to="`/product-details/${item.id}`" class="block flex-none" @click="closeSearch">
                            <img class="w-[64px] h-[64px] object-cover aspect-square" :src="item.image || fallbackImage" :alt="item.name" />
                        </router-link>
                        <div class="flex-1 min-w-0">
                            <router-link :to="`/product-details/${item.id}`" class="text-base font-semibold leading-snug text-title dark:text-white block truncate hover:text-primary" @click="closeSearch">
                                {{ item.name }}
                            </router-link>
                            <div class="mt-1 flex items-center gap-2">
                                <span class="text-sm font-medium text-primary">{{ item.price }}</span>
                                <span v-if="item.originalPrice" class="text-xs line-through text-paragraph dark:text-white-light">{{ item.originalPrice }}</span>
                            </div>
                            <span
                                v-if="item.inStock"
                                class="mt-1 inline-block rounded-full bg-green-100 px-2 py-1 text-[11px] leading-none text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            >
                                In stock
                            </span>
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <button class="relative hdr_wishList_btn" aria-label="wishlist" @click="toggleWishlist">
            <span v-if="wishlistCount" class="absolute w-[22px] h-[22px] bg-secondary top-[0px] -right-[11px] rounded-full flex items-center justify-center text-xs leading-none text-white">{{ wishlistCount }}</span>
            <i class="mdi mdi-cards-heart-outline text-title dark:text-white text-[24px] sm:text-[28px]"></i>
        </button>
        <div v-if="wishList" class="wishlist_popup w-80 md:w-96 absolute z-50 top-full right-0 sm:right-20 xl:right-11 bg-white dark:bg-title py-5 md:py-[30px] pl-5 md:pl-[30px] pr-[10px] md:pr-[15px] border border-primary">
            <h4 class="font-medium leading-none dark:text-white mb-4 text-xl md:text-2xl">Wishlist</h4>
            <div v-if="!isAuthenticated" class="pr-4 md:pr-5">
                <p class="text-sm md:text-base text-paragraph dark:text-white-light">Sign in to save products to your wishlist.</p>
                <div class="mt-5">
                    <router-link to="/login" class="btn btn-outline btn-sm w-full" data-text="Login">
                        <span>Login</span>
                    </router-link>
                </div>
            </div>
            <div v-else>
                <div v-if="wishlistItems.length" class="pr-4 md:pr-5 wishlist-item">
                    <div v-for="item in wishlistItems" :key="item.id" class="flex items-center gap-[15px] relative pb-[15px] mb-[15px] border-b border-bdr-clr dark:border-bdr-clr-drk">
                        <router-link :to="item.product_url" class="block">
                            <img class="w-[70px] h-[70px] object-cover" :src="item.image || fallbackImage" :alt="item.name">
                        </router-link>
                        <div class="flex-1 min-w-0">
                            <div class="flex items-center gap-2">
                                <span class="text-[14px] md:text-[15px] leading-none block truncate">{{ item.tag }}</span>
                                <span class="w-[6px] h-[6px] rounded-full bg-primary flex-none"></span>
                                <span class="text-[14px] md:text-[15px] leading-none block">{{ item.price }}</span>
                            </div>
                            <h6 class="text-base md:text-lg font-semibold leading-none mt-3 truncate">
                                <router-link :to="item.product_url">{{ item.name }}</router-link>
                            </h6>
                        </div>
                        <button type="button" class="wishList_item_close absolute top-0 right-0 w-6 h-6 flex items-center justify-center bg-title dark:bg-white bg-opacity-10 dark:bg-opacity-10 group duration-300 hover:bg-primary dark:hover:bg-primary" @click="removeWishlistItem(item.id)">
                            <svg class="fill-current text-title dark:text-white duration-300 group-hover:text-white" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0.636719 1.56306L1.56306 0.636719L4.98839 4.06204L8.41371 0.636719L9.31851 1.54152L5.89319 4.96685L9.3616 8.43526L8.43526 9.3616L4.96685 5.89319L1.54152 9.31851L0.636719 8.41371L4.06204 4.98839L0.636719 1.56306Z"/>
                            </svg>
                        </button>
                    </div>
                </div>
                <div v-else class="pr-4 md:pr-5">
                    <p class="text-sm md:text-base text-paragraph dark:text-white-light">Your wishlist is empty.</p>
                </div>
                <div class="mt-6 md:mt-10">
                    <router-link to="/wishlist" class="btn btn-outline btn-sm w-full" data-text="View All Wishlist" @click="wishList = false">
                        <span>View All Wishlist</span>
                    </router-link>
                </div>
            </div>
        </div>

        <button class="relative hdr_cart_btn" aria-label="cart" @click="toggleCart">
            <span v-if="cartCount" class="absolute w-[22px] h-[22px] bg-secondary top-[0px] -right-[11px] rounded-full flex items-center justify-center text-xs leading-none text-white">{{ cartCount }}</span>
            <span class="mdi mdi-shopping-outline text-title dark:text-white text-[24px] sm:text-[28px]"></span>
        </button>
        <div v-if="cartList" class="hdr_cart_popup w-80 md:w-96 absolute z-50 top-full right-0 sm:right-10 xl:right-0 bg-white dark:bg-title p-5 md:p-[30px] border border-primary">
            <h4 class="font-medium leading-none mb-4 text-xl md:text-2xl">Cart</h4>
            <div v-if="cartItems.length">
                <div class="hdr-cart-item">
                    <div v-for="item in cartItems" :key="item.cart_key" class="flex gap-[15px] relative pb-[15px] mb-[15px] border-b border-bdr-clr dark:border-bdr-clr-drk group">
                        <router-link :to="item.product_id ? `/product-details/${item.product_id}` : '/product-details'" class="block">
                            <img class="w-[70px] h-[70px] object-cover" :src="item.image || fallbackImage" :alt="item.name">
                        </router-link>
                        <div class="flex-1 min-w-0">
                            <div class="flex items-center gap-2">
                                <span class="text-[14px] md:text-[15px] leading-none block truncate">{{ item.category_name || 'Product' }}</span>
                                <span class="w-[6px] h-[6px] rounded-full bg-primary flex-none"></span>
                                <span class="text-[14px] md:text-[15px] leading-none block">{{ item.formattedUnitPrice }}</span>
                            </div>
                            <h6 class="text-base md:text-lg font-semibold leading-none mt-[10px] truncate">
                                <router-link :to="item.product_id ? `/product-details/${item.product_id}` : '/product-details'">{{ item.name }}</router-link>
                            </h6>
                            <div v-if="item.selected_modifiers.length" class="mt-2 flex flex-wrap gap-1">
                                <template v-for="modifier in item.selected_modifiers" :key="modifier.modifier_id">
                                    <template v-for="option in modifier.options" :key="`${modifier.modifier_id}-${option.option_id || option.label}`">
                                        <span v-if="modifier.is_color" class="inline-flex w-4 h-4 rounded-full border border-white/20" :title="option.label" :style="{ backgroundColor: option.value || '#D4D4D8' }"></span>
                                        <span v-else class="inline-flex rounded-full bg-[#F8F8F9] dark:bg-dark-secondary px-2 py-1 text-[11px] leading-none">{{ option.label }}</span>
                                    </template>
                                </template>
                            </div>
                            <div class="mt-3 flex items-center gap-2">
                                <button type="button" class="w-6 h-6 bg-[#E8E9EA] dark:bg-dark-secondary flex items-center justify-center" @click="updateCartQuantity(item, item.quantity - 1)">
                                    <svg class="fill-current text-title dark:text-white" width="12" height="2" viewBox="0 0 14 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10.4361 0.203613H12.0736L7.81774 0.203615H13.8729V1.80309H7.81774L3.50809 1.80309H1.87053L6.18017 1.80309H0.125V0.203615H6.18017L10.4361 0.203613Z"/>
                                    </svg>
                                </button>
                                <input class="w-6 h-auto outline-none bg-transparent text-base leading-none text-title dark:text-white text-center" type="text" :value="item.quantity" readonly>
                                <button type="button" class="w-6 h-6 bg-[#E8E9EA] dark:bg-dark-secondary flex items-center justify-center" @click="updateCartQuantity(item, item.quantity + 1)">
                                    <svg class="fill-current text-title dark:text-white" width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6.18017 0.110352H7.81774V6.16553H13.8729V7.76501H7.81774V13.8963H6.18017V7.76501H0.125V6.16553H6.18017V0.110352Z"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <button type="button" class="wishList_item_close absolute top-0 right-0 w-6 h-6 flex items-center justify-center bg-title dark:bg-white bg-opacity-10 dark:bg-opacity-10 group hover:bg-primary dark:hover:bg-primary opacity-0 group-hover:opacity-100 text-title dark:text-white duration-300 hover:text-white" @click="removeCartItem(item.cart_key)">
                            <svg class="fill-current" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0.636719 1.56306L1.56306 0.636719L4.98839 4.06204L8.41371 0.636719L9.31851 1.54152L5.89319 4.96685L9.3616 8.43526L8.43526 9.3616L4.96685 5.89319L1.54152 9.31851L0.636719 8.41371L4.06204 4.98839L0.636719 1.56306Z"/>
                            </svg>
                        </button>
                    </div>
                </div>
                <div class="pt-5 md:pt-[30px] mt-5 md:mt-[30px] border-t border-bdr-clr dark:border-bdr-clr-drk">
                    <h4 class="mb-5 md:mb-[30px] font-medium !leading-none text-lg md:text-xl text-right">Subtotal : {{ formattedSubtotal }}</h4>
                    <div class="grid grid-cols-2 gap-4">
                        <router-link to="/cart" class="btn btn-outline btn-sm" data-text="View Cart" @click="cartList = false">
                            <span>View Cart</span>
                        </router-link>
                        <router-link to="/checkout" class="btn btn-theme-solid btn-sm" data-text="Checkout" @click="cartList = false">
                            <span>Checkout</span>
                        </router-link>
                    </div>
                </div>
            </div>
            <div v-else>
                <p class="text-sm md:text-base text-paragraph dark:text-white-light">Your cart is empty.</p>
                <div class="mt-5">
                    <router-link to="/shop-v2" class="btn btn-outline btn-sm w-full" data-text="Browse Products" @click="cartList = false">
                        <span>Browse Products</span>
                    </router-link>
                </div>
            </div>
        </div>

        <button class="hamburger" :class="toggle ? 'opened' : ''" @click="handleToggle">
            <svg class="stroke-current text-title dark:text-white" width="40" viewBox="0 0 100 100">
                <path class="line line1" d="M 20,29.000046 H 80.000231 C 80.000231,29.000046 94.498839,28.817352 94.532987,66.711331 94.543142,77.980673 90.966081,81.670246 85.259173,81.668997 79.552261,81.667751 75.000211,74.999942 75.000211,74.999942 L 25.000021,25.000058" />
                <path class="line line2" d="M 20,50 H 80" />
                <path class="line line3" d="M 20,70.999954 H 80.000231 C 80.000231,70.999954 94.498839,71.182648 94.532987,33.288669 94.543142,22.019327 90.966081,18.329754 85.259173,18.331003 79.552261,18.332249 75.000211,25.000058 75.000211,25.000058 L 25.000021,74.999942" />
            </svg>
        </button>
        <div class="w-[1px] bg-title/20 dark:bg-white/20 h-7 hidden sm:block"></div>
        <SwitcherS/>
    </div>
</template>

<script setup>
    import { computed, defineEmits, defineProps, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
    import { useRouter } from 'vue-router';
    import { PRODUCT_PLACEHOLDER as fallbackImage } from '@/lib/placeholder'
    import SwitcherS from '../switcher-s.vue'
    import { useClientSession } from '@/composables/use-client-session';
    import { useCart } from '@/composables/use-cart';
    import { useWishlist } from '@/composables/use-wishlist';
    import { mapProductCard, storefrontRequest } from '@/lib/storefront-api';

    const router = useRouter();
    const wishList = ref(false)
    const cartList = ref(false);
    const searchOpen = ref(false);
    const searchQuery = ref('');
    const searchResults = ref([]);
    const searchLoading = ref(false);
    const searchError = ref('');
    const searchPopupRef = ref(null);
    const searchInputRef = ref(null);
    let searchDebounceTimer = null;
    let searchRequestId = 0;
    const session = useClientSession();
    const cart = useCart();
    const wishlist = useWishlist();

    const isAuthenticated = computed(() => session.isAuthenticated.value);
    const displayName = computed(() => {
        const name = session.state.user?.name?.trim();
        return name ? name.split(' ')[0] : 'Account';
    });
    const userInitial = computed(() => displayName.value.charAt(0) || 'A');
    const wishlistItems = computed(() => wishlist.items.value);
    const wishlistCount = computed(() => wishlist.count.value);
    const cartItems = cart.items;
    const cartCount = cart.cartCount;
    const formattedSubtotal = cart.formattedSubtotal;

    const props = defineProps({
        toggle: Boolean,
    });

    const emit = defineEmits(['toggle-change']);

    function handleToggle() {
        emit('toggle-change', !props.toggle);
    }

    function toggleWishlist() {
        cartList.value = false;
        searchOpen.value = false;
        if (!isAuthenticated.value) {
            router.push('/login');
            return;
        }
        wishList.value = !wishList.value;
    }

    function toggleCart() {
        wishList.value = false;
        searchOpen.value = false;
        cartList.value = !cartList.value;
    }

    function toggleSearch() {
        wishList.value = false;
        cartList.value = false;
        searchOpen.value = !searchOpen.value;
        if (searchOpen.value) {
            nextTick(() => searchInputRef.value?.focus());
        }
    }

    function closeSearch() {
        searchOpen.value = false;
    }

    async function runSearch(term) {
        const requestId = ++searchRequestId;
        searchLoading.value = true;
        searchError.value = '';
        try {
            const response = await storefrontRequest('/products', {
                params: { search: term, per_page: 8 },
            });
            if (requestId !== searchRequestId) return;
            searchResults.value = (response.data || []).map((product) => {
                const card = mapProductCard(product);
                const stockQuantity = Number(product.stock_quantity || 0);
                return {
                    ...card,
                    stockQuantity: Math.floor(stockQuantity),
                    inStock: stockQuantity > 0,
                };
            });
        } catch (error) {
            if (requestId !== searchRequestId) return;
            searchError.value = error.message || 'Search failed.';
            searchResults.value = [];
        } finally {
            if (requestId === searchRequestId) {
                searchLoading.value = false;
            }
        }
    }

    watch(searchQuery, (value) => {
        if (searchDebounceTimer) {
            clearTimeout(searchDebounceTimer);
            searchDebounceTimer = null;
        }
        const term = value.trim();
        if (!term) {
            searchResults.value = [];
            searchLoading.value = false;
            searchError.value = '';
            return;
        }
        searchDebounceTimer = setTimeout(() => runSearch(term), 300);
    });

    function handleOutsideClick(event) {
        if (!searchOpen.value) return;
        const popup = searchPopupRef.value;
        const trigger = event.target.closest?.('.hdr_search_btn');
        if (trigger) return;
        if (popup && !popup.contains(event.target)) {
            searchOpen.value = false;
        }
    }

    async function removeWishlistItem(productId) {
        await wishlist.remove(productId);
    }

    function updateCartQuantity(item, quantity) {
        cart.updateQuantity(item.cart_key, Math.max(1, quantity));
    }

    function removeCartItem(cartKey) {
        cart.removeItem(cartKey);
    }

    watch(isAuthenticated, async (authenticated) => {
        if (authenticated) {
            await wishlist.load(true);
        } else {
            wishlist.reset();
        }
    });

    onMounted(async () => {
        document.addEventListener('mousedown', handleOutsideClick);

        try {
            await session.initializeSession();
        } catch (error) {
            // Let the session composable clear invalid tokens quietly.
        }

        cart.initializeCart();

        if (session.isAuthenticated.value) {
            await wishlist.load(true);
        }
    });

    onBeforeUnmount(() => {
        document.removeEventListener('mousedown', handleOutsideClick);
        if (searchDebounceTimer) {
            clearTimeout(searchDebounceTimer);
        }
    });
</script>
